import dayjs from "dayjs";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import useFindOrder from "@repo/shopify/hooks/useFindOrder";
import useOrderDetail from "@repo/shopify/hooks/useOrderDetail";
import useOrderInfo from "@repo/shopify/hooks/useOrderInfo";
import useOrderPayMethod from "@repo/shopify/hooks/useOrderPayMethod";
import useOrderStatus from "@repo/shopify/hooks/useOrderStatus";
import { OrderStatusButtonType } from "@repo/shopify/types/enum";
import { formatOrderAddress } from "@repo/shopify/utils/address";
import { OrderStatusTrans, getTrackingInfo } from "@repo/shopify/utils/order";

import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import Address from "@molecules/Address";
import OrderLineItem from "@molecules/OrderLineItem";

import OrderPaymentInfo from "@organisms/OrderPaymentInfo";
import OrderStatusButton from "@organisms/OrderStatusButton";

import { DEFAULT_TITLE } from "@const/meta";
import { URLS } from "@const/urls";

import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

import { formatAutoDiscountTitle } from "@utils/discount";

export default function OrderDetail() {
  const navigate = useNavigate();
  const { handle } = useParams();

  const { customer, token } = useCustomerStore();
  const { setMeta, openAccount, showFooter } = useLayoutStore();

  const { lang } = useLanguage();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
    showFooter(true);
    setMeta({ title: `Order Detail - ${DEFAULT_TITLE}` });
  }, []);

  useEffect(() => {
    if (!token?.accessToken) {
      setTimeout(() => {
        sessionStorage.setItem("loginAfterUrl", `${URLS.ORDER}${handle}`);
        openAccount(true);
      }, 300);
    }
  }, [token]);

  const { order, loading, orderNotFound } = useFindOrder({ orderId: handle, accessToken: token.accessToken });

  const { completeOrder, refundOrder, returnOrder, totalReturnShippingFee, orderInfo, isLoading } = useOrderDetail({
    lang,
    customer,
    handle,
    order,
    orderNotFound,
  });

  useEffect(() => {
    if (!(loading || isLoading) && !order?.id && !orderInfo) {
      navigate(URLS.NOT_FOUND, { replace: true });
    }
  }, [order, loading, isLoading, orderInfo]);

  // 결제 수단 정보
  const paymentInfo = useOrderPayMethod({ lang, order });

  const { activeButton, orderStatus } = useOrderStatus(order, lang === "ko");

  // 주문 정보
  const {
    shippingAmount,
    taxAmount,
    dutyAmount,
    totalAmount,
    subTotalAmount,
    refundAmount,
    codeDiscountAmount,
    autoDiscountAmount,
    codeDiscountTitle,
    autoDiscountTitle,
    shippingTitle,
  } = useOrderInfo(order, orderInfo);

  // 배송 송장 정보
  const trackingInfo = getTrackingInfo({ order, lang });

  const estimatedDeliveryRaw = order?.customAttributes.find((a) => a.key === "estimatedDelivery")?.value;
  const estimatedDeliveryDate = estimatedDeliveryRaw ? dayjs(estimatedDeliveryRaw).format("MMM DD, YYYY") : null;

  // 배송지 정보
  const shippingAddress = formatOrderAddress({ address: order?.shippingAddress, lang });
  const billingAddress = formatOrderAddress({ address: order?.billingAddress, lang });

  return (
    <>
      {!order || loading || isLoading ? (
        <Loading type="page" />
      ) : (
        <section className="pc:col-span-10 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-10">
          <section className="pc:col-span-10 pc:flex-col col-span-8 flex justify-between gap-5">
            <section className="pc:text-[1.125rem] flex flex-col gap-1 text-lg leading-none">
              <h2>{order.name || ""}</h2>
              <div className="pc:flex-row pc:gap-2.5 flex flex-col gap-2">
                <span>{dayjs(order?.processedAt).format("MMM DD, YYYY")}</span>
                <div className="flex gap-2.5">
                  <span className="text-md pc:text-lg leading-none">{orderStatus ? t(OrderStatusTrans[orderStatus]) : orderStatus}</span>
                  {trackingInfo ? (
                    <Link as="text" className="pc:text-lg! w-fit leading-none font-normal underline" href={trackingInfo.url}>
                      {trackingInfo.number}
                    </Link>
                  ) : null}
                </div>
              </div>
            </section>

            {activeButton !== OrderStatusButtonType.NONE && handle ? (
              <OrderStatusButton buttonType={activeButton} className="px-3!" orderId={handle} />
            ) : null}
          </section>

          <section className="pc:col-span-10 pc:gap-y-0 col-span-8 grid grid-cols-subgrid gap-y-10">
            <section className="pc:col-span-3 col-span-8 col-start-1 flex flex-col gap-5">
              <div className="flex flex-col gap-y-1">
                <span>Shipping Method</span>
                <span className="font-normal">{shippingTitle}</span>
              </div>
              <div className="flex flex-col gap-y-1">
                <span>Payment Detail</span>
                <span className="font-normal">{paymentInfo}</span>
              </div>
              <div className="flex flex-col gap-y-1">
                <span>Shipping Address</span>
                <Address {...shippingAddress} className="font-normal" />
              </div>
              <div className="flex flex-col gap-y-1">
                <span>Billing Address</span>
                <Address {...billingAddress} className="font-normal" />
              </div>
              {String(order.fulfillmentStatus) !== "FULFILLED" && estimatedDeliveryDate ? (
                <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                  <span>Estimated Delivery Date</span>
                  <span className="font-normal">{estimatedDeliveryDate}</span>
                </div>
              ) : null}
            </section>

            <section className="pc:col-span-5 pc:col-start-4 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-10 leading-none">
              {/* 반품 내역 - (returnRequested, returnApproved) */}
              {returnOrder.length > 0 ? (
                <div className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-2.5">
                  <span className="pc:col-span-5 col-span-8">Return Items</span>
                  <ul className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-4">
                    {returnOrder.map((node) => (
                      <OrderLineItem key={node.id} node={node} />
                    ))}
                  </ul>
                </div>
              ) : null}

              {/* 주문 진행 내역 - (unfulfilled, fulfilled, onhold) */}
              {completeOrder.length > 0 ? (
                <div className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-2.5">
                  <span className="pc:col-span-5 col-span-8">Line items</span>
                  <ul className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-4">
                    {completeOrder.map((node) => (
                      <OrderLineItem key={node.id} node={node} />
                    ))}
                  </ul>
                </div>
              ) : null}

              {/* 환불 내역 - (canceled, refunded)*/}
              {refundOrder.length > 0 ? (
                <div className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-2.5">
                  <span className="pc:col-span-5 col-span-8">Refunded Items</span>
                  <ul className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-4">
                    {refundOrder.map((node) => (
                      <OrderLineItem key={node.id} node={node} />
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="pc:items-end pc:col-start-4 col-span-8 flex w-full flex-col">
                <div className="pc:w-screen-col-span-2 flex flex-col gap-1">
                  <OrderPaymentInfo
                    autoDiscountAmount={autoDiscountAmount}
                    autoDiscountTitle={formatAutoDiscountTitle(autoDiscountTitle)}
                    codeDiscountAmount={codeDiscountAmount}
                    codeDiscountTitle={codeDiscountTitle}
                    dutyAmount={dutyAmount}
                    originalTotalAmount={subTotalAmount}
                    refundAmount={refundAmount}
                    returnShippingFee={totalReturnShippingFee ?? 0}
                    shippingFee={shippingAmount}
                    taxAmount={taxAmount}
                    totalAmount={totalAmount}
                  />
                </div>
              </div>
            </section>
          </section>
        </section>
      )}
    </>
  );
}
